import { Injectable } from "@angular/core";
import { UserSession } from "@modules/api/user-session.generated";
import { CrossTabCommunicationService } from "@modules/common/services/cross-tab-communication.service";
import { TabRegistrationService } from "@modules/common/services/tab-registration.service";
import { Observable, of as observableOf, ReplaySubject } from 'rxjs';

/**
 * Service to maintain the client-side copy of 
 * the user's session information.
 */
@Injectable({
    providedIn: 'root'
})
export class UserSessionService {
    constructor(
        private _crossTabCommunicationService: CrossTabCommunicationService,
        private _tabRegistrationService: TabRegistrationService
    ) { }

    /**
     * Initialize the user session service. Called during app start.
     * This function loads the user session stored in sessionStorage
     * and initializes all of the mechanisms used to synchronize
     * sessions across browser tabs.
     */
    public initialize(): Promise<any> {
        this._tabRegistrationService.initialize();
        this.wireupCrossTabCommunications();
        this.loadSavedUserSession();
        //If we couldn't load a user session from session storage
        //and there's more than one tab open, then let's wait a
        //little while to see if the other tab will send us the
        //session info, which was requested in loadSavedUserSession().
        if (!this._userSession
            && this._tabRegistrationService.getInstanceCount() > 1
            && localStorage.getItem('is-user-logged-in')) {            
            return new Promise<any>(resolve => {
                this._initializationPromiseResolver = resolve;

                //Give the other tabs 10 seconds to send us the session info
                //and if we don't have it by then then just give up.

                if (!this._initializationTimeout) {
                    this._initializationTimeout = setTimeout(() => {
                        if (this._initializationPromiseResolver) {
                            this._initializationPromiseResolver(undefined);
                        }

                        this._initializationTimeout = undefined;
                        this._userSessionSubject.next(this._userSession);

                    }, 10000); // 10 seconds
                }
            });            
        } else {
            this._userSessionSubject.next(this._userSession);
        }

        return new Promise<any>(resolve => resolve(undefined));
    }

    private _initializationPromiseResolver?: (value: any) => void;
    private _initializationTimeout: any;
    private _userSession?: UserSession;

    /**
     * Load the user session saved in session storage
     * and if it doesn't exist then send a cross tab
     * request to get the info from another tab.
     */
    private loadSavedUserSession(): void {

        const json = sessionStorage.getItem('userSession');
        this._userSession = json && this.jsonParse(json);

        if (!this._userSession) {
            this._crossTabCommunicationService.sendMessage({
                key: 'GET_SESSION',
                msg: {}
            });
        }
    }

    /**
     * Register event handlers for cross tab commmunications.
     */
    private wireupCrossTabCommunications(): void {
        this._crossTabCommunicationService.register({
            key: 'GET_SESSION',
            handler: () => this._crossTabCommunicationService.sendMessage({
                key: 'SET_SESSION',
                msg: this.getUserSession()
            })
        });

        this._crossTabCommunicationService.register({
            key: 'SET_SESSION',
            handler: e => e.newValue && this.internalSetUserSession(this.jsonParse(e.newValue), false)
        });
    }

    /**
     * Retrieve the current UserSession instance, if
     * there is one. Returns undefined if no session
     * info exists. Not that this function still
     * returns the session even if it's expired.
     */
    public getUserSession(): UserSession | undefined {
        return this._userSession;
    }

    /**
     * A subscribable version of getUserSession()
     */
    public userSessionChanged(): Observable<UserSession | undefined> {
        return this._userSessionSubject;
    }

    /**
     * Updates the global UserSession object.
     * @param userSession The new UserSession object.
     */
    public setUserSession(userSession: UserSession): void {
        this.internalSetUserSession(userSession, true);
    }

    /**
     * Signs out the current user and destroys their session.
     */
    public signOut(): Observable<any> {
        this.internalSetUserSession(undefined, true);
        return observableOf([]);
    }

    private internalSetUserSession(userSession?: UserSession, broadcast?: boolean) {
        this._userSession = userSession;
        if (userSession) {
            localStorage.setItem('is-user-logged-in', 'true');
            sessionStorage.setItem('userSession', JSON.stringify(userSession));
        } else {
            localStorage.removeItem('is-user-logged-in');
            sessionStorage.removeItem('userSession');
        }
        if (broadcast) {
            this._crossTabCommunicationService.sendMessage({
                key: 'SET_SESSION',
                msg: this._userSession
            });
        }

        //If this function is being called during initialize()
        //then we also need to resolve the initializer promise.
        if (this._initializationPromiseResolver) {
            this._initializationPromiseResolver(undefined);
            this._initializationPromiseResolver = undefined;
        }
        //and cancel the initialize() timeout.
        if (this._initializationTimeout) {
            clearTimeout(this._initializationTimeout);
            this._initializationTimeout = undefined;
        }

        this._userSessionSubject.next(userSession);
    }

    private jsonParse(json: string): any {
        try {
            return JSON.parse(json);
        } catch (ex) { }
    }

    private _userSessionSubject = new ReplaySubject<UserSession | undefined>(1);
}
